/**
 * `--model auto`: per-turn model routing. A classifier (Jev by default — injected, so a regex or
 * cheap-model classifier can replace it) picks a capability tier — basic | standard | premium — for each user turn (tiers map to any provider's models).
 *
 * The MAIN session only ever runs on basic/standard: switching it to premium throws away the prompt cache.
 * A hard turn is instead OFFLOADED to a premium sub-agent (same child machinery as the `Task` tool):
 * the main model writes a brief, the last 2 turns ride along verbatim, and the premium answer is appended
 * to the main transcript as that turn's reply. The premium side-conversation is kept in `state` and
 * RESUMED (delta + new question only) when the classifier says the turn continues the same thread.
 *
 * Classifier failure/timeout ⇒ standard (logged). `state` is plain JSON so a host can persist it
 * across processes (the CLI stores it in the session, so `-c`/`--resume` keeps the premium thread).
 */
import { Agent, type RunResult } from './Agent';
import { contentText, type Message, type MessageContent } from './llm';
import { childOptionsFor } from './subagent';
import { forComponent } from './logging';
import { latestClaude } from './models';

const log = forComponent('ModelRouter');

export type Tier = 'basic' | 'standard' | 'premium';
export interface Turn { user: string; assistant: string }
export interface RouteInput { previousModel: 'basic' | 'standard'; history: Turn[]; next: string; lastOffload?: string }
/** Classifier output: tier probabilities (+ the raw argmax) and, when asked, P(same thread as last offload). */
export interface Classification { probs: Partial<Record<Tier, number>>; choice: Tier; sameThread?: number }
export type Classifier = (input: RouteInput) => Promise<Classification>;

export interface RouteDecision {
  tier: Tier; model: string; probs: Partial<Record<Tier, number>>; choice?: Tier;
  sameThread?: number; offload?: 'fresh' | 'continue'; fallback?: string; classifyMs: number;
}
export interface RouterState {
  main?: string;
  offload?: { messages: Message[]; mainLen: number; question: string };
  offloads?: number; reuses?: number;
}

type Usage = NonNullable<RunResult['usage']>;

export class ModelRouterOptions {
  classifier: Classifier = jevClassifier();
  /** Capability tiers → model id (any provider). basic/standard run the main session; premium is only ever an offload. */
  models: Record<Tier, string> = { basic: latestClaude('haiku'), standard: latestClaude('sonnet'), premium: latestClaude('opus') };
  /** Escalate to a stronger tier when it holds at least this much probability mass (upward bias). */
  threshold = 0.25;
  /** P(same thread) at or above which the previous premium side-conversation is resumed. */
  sameThreadAt = 0.5;
  classifyTimeoutMs = 10_000;
  /** Host-supplied pricing (model, usage) → USD, so mixed-model turns are costed per model. */
  costOf: (model: string, usage?: Usage) => number = () => 0;
  /** Step budget for the premium sub-agent. */
  offloadMaxSteps = 40;
  /** Host-supplied provider options per model (e.g. claude-code's subscription env); applied on every tier switch + the offload child. */
  providerOptionsFor?: (model: string) => Record<string, unknown> | undefined;
}

/** Upward-biased tier pick: premium if P(premium) ≥ th; standard over basic if P(standard)+P(premium) ≥ th. */
export function pickTier(c: Classification, th: number): Tier {
  const o = c.probs.premium ?? 0, s = c.probs.standard ?? 0;
  if (o >= th || c.choice === 'premium') return 'premium';
  if (c.choice === 'standard' || s + o >= th) return 'standard';
  return 'basic';
}

/** User/assistant text turns of a transcript (tool traffic dropped), oldest first. */
export function textTurns(msgs: Message[]): Turn[] {
  const out: Turn[] = [];
  for (const m of msgs) {
    if (m.role === 'user') {
      const t = contentText(m.content).trim();
      if (t) out.push({ user: t, assistant: '' });
    } else if (m.role === 'assistant' && out.length) {
      const t = contentText(m.content).trim();
      if (t) out[out.length - 1].assistant = (out[out.length - 1].assistant + '\n' + t).trim();
    }
  }
  return out;
}

const fmtTurns = (ts: Turn[]) => ts.map((t) => `USER: ${t.user}\nASSISTANT: ${t.assistant || '(no text reply)'}`).join('\n\n');
const addUsage = (a: Usage | undefined, b: Usage | undefined): Usage | undefined => {
  if (!a) return b; if (!b) return a;
  return {
    promptTokens: a.promptTokens + b.promptTokens, completionTokens: a.completionTokens + b.completionTokens, totalTokens: a.totalTokens + b.totalTokens,
    cacheCreationTokens: (a.cacheCreationTokens ?? 0) + (b.cacheCreationTokens ?? 0), cacheReadTokens: (a.cacheReadTokens ?? 0) + (b.cacheReadTokens ?? 0),
  };
};

export class ModelRouter {
  public state: RouterState = {};
  public constructor(public options?: Partial<ModelRouterOptions>) {
    this.options = { ...new ModelRouterOptions(), ...options };
  }
  private get o() { return this.options as ModelRouterOptions; }

  /** Classify one turn. Never throws: classifier failure/timeout ⇒ standard. */
  async decide(transcript: Message[], next: string): Promise<RouteDecision> {
    const o = this.o;
    const main = this.state.main === o.models.basic ? 'basic' : 'standard';
    const input: RouteInput = {
      previousModel: main,
      history: textTurns(transcript).slice(-4).map((t) => ({ user: t.user.slice(0, 1500), assistant: t.assistant.slice(0, 1500) })),
      next, lastOffload: this.state.offload?.question,
    };
    const t0 = performance.now();
    let d: RouteDecision;
    try {
      let timer: ReturnType<typeof setTimeout> | undefined;
      const c = await Promise.race([
        o.classifier(input),
        new Promise<never>((_, rej) => { timer = setTimeout(() => rej(new Error(`classifier timeout ${o.classifyTimeoutMs}ms`)), o.classifyTimeoutMs); }),
      ]).finally(() => clearTimeout(timer));
      const tier = pickTier(c, o.threshold);
      d = { tier, model: o.models[tier], probs: c.probs, choice: c.choice, sameThread: c.sameThread, classifyMs: Math.round(performance.now() - t0) };
      if (tier === 'premium') d.offload = this.state.offload && (c.sameThread ?? 0) >= o.sameThreadAt ? 'continue' : 'fresh';
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      log.warn(`classifier failed, falling back to standard: ${msg}`);
      d = { tier: 'standard', model: o.models.standard, probs: {}, fallback: msg, classifyMs: Math.round(performance.now() - t0) };
    }
    log.info(`route ${d.tier}${d.offload ? `(${d.offload})` : ''} · argmax=${d.choice ?? '-'} · p=${JSON.stringify(d.probs)}${d.sameThread != null ? ` · same=${d.sameThread.toFixed(2)}` : ''} · ${d.classifyMs}ms`);
    return d;
  }

  /** Run one user turn on `agent` with routing. Returns a RunResult carrying `costUsd` + `routing`. */
  async send(agent: Agent, content: MessageContent): Promise<RunResult> {
    const o = this.o;
    const text = contentText(content);
    const d = await this.decide(agent.transcript, text);
    if (d.tier !== 'premium') {
      this.use(agent, this.state.main = d.model);
      const res = await agent.send(content);
      return { ...res, costUsd: o.costOf(d.model, res.usage), routing: d };
    }
    this.use(agent, this.state.main ??= o.models.standard);
    return this.offload(agent, content, text, d);
  }

  private async offload(agent: Agent, content: MessageContent, text: string, d: RouteDecision): Promise<RunResult> {
    const o = this.o, main = this.state.main!;
    const prev = this.state.offload;
    const cont = d.offload === 'continue' && prev && prev.mainLen <= agent.transcript.length;
    let briefUsage: Usage | undefined, prompt: string;
    if (cont) {
      const delta = textTurns(agent.transcript.slice(prev.mainLen));
      prompt = `Follow-up in the same thread.${delta.length ? ` Since your last answer the main conversation continued:\n\n${fmtTurns(delta)}\n` : ''}\n\nNew request:\n${text}`;
    } else {
      const b = await this.brief(agent, text);
      briefUsage = b.usage;
      const last2 = textTurns(agent.transcript).slice(-2);
      prompt = `You are handling a hard turn delegated by the main agent of an ongoing session. You do NOT see its conversation — only this brief. Do the work with your tools, then reply to the user directly (your reply is shown as the session's answer).\n\n--- brief from the main agent ---\n${b.text}\n\n${last2.length ? `--- last turns (verbatim) ---\n${fmtTurns(last2)}\n\n` : ''}--- current user request (verbatim) ---\n${text}`;
    }
    const child = this.spawn(agent);
    const res = cont ? (child.transcript = prev.messages, await child.send(prompt)) : await child.run(prompt);
    this.state.offloads = (this.state.offloads ?? 0) + 1;
    if (cont) this.state.reuses = (this.state.reuses ?? 0) + 1;
    const answer = res.text || `(premium sub-agent finished with no reply; finishReason=${res.finishReason})`;
    agent.transcript.push({ role: 'user', content }, { role: 'assistant', content: answer });
    this.state.offload = { messages: child.transcript, mainLen: agent.transcript.length, question: text };
    log.info(`offload ${cont ? 'continue' : 'fresh'} done · ${res.finishReason} · ${res.steps} steps`);
    const costUsd = o.costOf(main, briefUsage) + o.costOf(o.models.premium, res.usage);
    return { ...res, text: answer, messages: agent.transcript, usage: addUsage(briefUsage, res.usage), costUsd, routing: d };
  }

  /** The main model writes the task brief on its own (cached) context, tools off; the transcript is restored after. */
  private async brief(agent: Agent, text: string): Promise<{ text: string; usage?: Usage }> {
    const snap = agent.transcript.slice();
    const { host, toolChoice, maxSteps } = agent.options;
    Object.assign(agent.options, { host: undefined, toolChoice: 'none', maxSteps: 1 });
    try {
      const r = await agent.send(`[router] The next request is being delegated to a stronger sub-agent that will NOT see this conversation. Write the self-contained brief you would hand it (<=200 words): the task, the relevant facts/files/decisions established so far, constraints. Output only the brief.\n\nThe request:\n${text}`);
      return { text: r.text.trim() || text, usage: r.usage };
    } catch (e) {
      log.warn(`brief failed, sending the raw request: ${e instanceof Error ? e.message : String(e)}`);
      return { text };
    } finally {
      agent.transcript = snap;
      Object.assign(agent.options, { host, toolChoice, maxSteps });
    }
  }

  private use(agent: Agent, model: string) {
    agent.options.model = model;
    if (this.o.providerOptionsFor) agent.options.providerOptions = this.o.providerOptionsFor(model);
  }

  private spawn(agent: Agent): Agent {
    const a = agent.options;
    const co = childOptionsFor({ ai: a.ai, model: a.model, fs: a.fs!, tools: a.tools, systemPrompt: a.systemPrompt, hooks: a.hooks, maxSteps: this.o.offloadMaxSteps, traceDir: a.traceDir, providerOptionsFor: this.o.providerOptionsFor },
      a.fs!, a.depth ?? 0, a.maxDepth ?? 2, undefined, this.o.models.premium);
    if (typeof co === 'string') throw new Error(co);
    co.signal = a.signal;
    return new Agent(co);
  }
}

/** Default classifier: Jev (TypeSafe). The module is loaded lazily from `jevModule`
 *  (default `@livx.cc/mcp-jev/jev`, or `$AGENTX_JEV_MODULE`); provider keys come from `env`,
 *  optionally merged with a dotenv file at `$AGENTX_JEV_ENV`. Any load/eval error ⇒ router falls back. */
type JevOpts = { jevModule?: string; envFile?: string; provider?: string };
const JEV_KEYS = ['AI_GATEWAY_API_KEY', 'OPENROUTER_API_KEY', 'TYPESAFE_API_KEY'];

/** Load the Jev module + its provider env (process env merged with the optional dotenv file). */
async function loadJev(opts: JevOpts) {
  const mod = await import(opts.jevModule ?? process.env.AGENTX_JEV_MODULE ?? '@livx.cc/mcp-jev/jev');
  const env: Record<string, string | undefined> = { ...process.env };
  const file = opts.envFile ?? process.env.AGENTX_JEV_ENV;
  if (file) {
    const { readFileSync } = await import('node:fs');
    for (const l of readFileSync(file, 'utf8').split('\n')) {
      const m = l.match(/^\s*([A-Z0-9_]+)\s*=\s*(.*?)\s*$/);
      if (m && !env[m[1]]) env[m[1]] = m[2].replace(/^['"]|['"]$/g, '');
    }
  }
  return { mod, env };
}

/** True when Jev can actually run here (module resolves + at least one provider key) — gates `auto` as the default model. */
export async function jevAvailable(opts: JevOpts = {}): Promise<boolean> {
  try {
    const { env } = await loadJev(opts);
    return JEV_KEYS.some((k) => env[k]?.trim());
  } catch (e) {
    log.debug(`jev unavailable: ${e instanceof Error ? e.message : String(e)}`);
    return false;
  }
}

export function jevClassifier(opts: JevOpts = {}): Classifier {
  let router: Promise<{ evaluate: (req: any, provider?: string) => Promise<any> }> | undefined;
  const load = async () => {
    const { mod, env } = await loadJev(opts);
    return new mod.JevRouter({ env });
  };
  return async (input) => {
    const jev = await (router ??= load().catch((e) => { router = undefined; throw e; }));
    const questions: any[] = [{
      id: 'tier', type: 'choice',
      instructions: 'Pick the CHEAPEST model that will handle the NEXT assistant turn correctly for this coding-agent conversation. Staying on previousModel keeps its prompt cache warm; switch only when the next turn clearly needs more (or much less) capability.',
      criteria: {
        basic: 'Trivial: commands, lookups, acknowledgements, commits, mechanical single-file edits, short rewrites.',
        standard: 'Standard engineering: features, bounded refactors, reviews, tests, following a clear trace, summarising.',
        premium: 'Hard: root-causing subtle/intermittent bugs, concurrency, security semantics, architecture trade-offs, quantitative reasoning.',
      },
    }];
    if (input.lastOffload) questions.push({
      id: 'sameThread', type: 'boolean',
      instructions: 'Does nextUserMessage continue the same line of work as lastOffload (the previous hard request, handled by a specialist) — a follow-up, refinement or next step of it — rather than a new, unrelated topic?',
    });
    const state = { previousModel: input.previousModel, history: input.history, nextUserMessage: input.next, ...(input.lastOffload ? { lastOffload: input.lastOffload } : {}) };
    const r = await jev.evaluate({ state, questions }, opts.provider);
    const a = r.answers.tier as { choice: string; probabilities?: Record<string, number> };
    const map = (k: string): Tier => k as Tier;
    const probs: Partial<Record<Tier, number>> = {};
    for (const [k, v] of Object.entries(a.probabilities ?? {})) probs[map(k)] = v;
    const st = r.answers.sameThread as { probability?: number; value?: boolean } | undefined;
    return { choice: map(a.choice), probs, sameThread: st ? (st.probability ?? (st.value ? 1 : 0)) : undefined };
  };
}
